import { FC } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import type { TFunction } from 'i18next';
import Logger from 'utils/logger';
import NumberUtil from 'utils/number';
import PostTimeSaversApi, {
  ApprovalRecipientFailure,
  PostWorkflowBatchResult,
  SendForApprovalParams,
  SendForApprovalResult,
} from 'app/api/postTimeSavers';
import { useToast } from 'app/hooks/useToast';
import { queryKey } from 'constants/queryKey';
import { ApiClientError } from 'api/client';
import { getErrorCode, getErrorResponse } from 'app/utils/apiError';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { UserActions } from 'modules/user/userActions';
import { FirstTimeFlagKey } from 'types/User';
import { useUser } from 'modules/user/userSelector';
import { getUserFirstTimeFlagStatus } from 'utils/user/firstTimeFlags';
import { useFirstApprovalRequestedModal } from 'app/components/modals/firstTimeFlags/firstApprovalRequested/FirstApprovalRequestedModal';
import {
  hasPostSentForApproval,
  markPostSentForApproval,
} from 'app/modules/onboardingChecklist/utils/sentForApprovalStorage';

type Reason = { key: string; defaultValue: string };

const GENERIC_REASON: Reason = {
  key: 'sendForApprovalResult.failureReason.generic',
  defaultValue: "Some posts couldn't be sent for approval.",
};

const REASON_BY_ERROR_CODE: Record<string, Reason> = {
  invalid_source_status: {
    key: 'sendForApprovalResult.failureReason.invalidStatus',
    defaultValue:
      "Some posts couldn't be sent because they can't be sent for approval from their current status.",
  },
  access_denied: {
    key: 'sendForApprovalResult.failureReason.accessDenied',
    defaultValue:
      "Some posts couldn't be sent because you don't have access to them.",
  },
  post_not_found: {
    key: 'sendForApprovalResult.failureReason.notFound',
    defaultValue: "Some posts couldn't be sent because they no longer exist.",
  },
  missing_permission: {
    key: 'sendForApprovalResult.failureReason.missingPermission',
    defaultValue:
      "Some posts couldn't be sent because you don't have permission to send them for approval.",
  },
};

function isBatchResult(value: unknown): value is PostWorkflowBatchResult {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as PostWorkflowBatchResult).failed) &&
    Array.isArray((value as PostWorkflowBatchResult).succeeded)
  );
}

export function buildSendForApprovalFailureMessage(
  result: unknown,
  t: TFunction,
): string | null {
  if (!isBatchResult(result) || result.failed.length === 0) {
    return null;
  }

  const total = result.succeeded.length + result.failed.length;

  const summary = t('sendForApprovalResult.summary', {
    defaultValue: '{{succeeded}} of {{total}} posts sent for approval.',
    succeeded: result.succeeded.length,
    total,
  });

  const seenKeys = new Set<string>();
  const reasons = result.failed
    .map(
      (failure) =>
        REASON_BY_ERROR_CODE[failure.error_code ?? ''] ?? GENERIC_REASON,
    )
    .filter((reason) => {
      if (seenKeys.has(reason.key)) {
        return false;
      }
      seenKeys.add(reason.key);
      return true;
    })
    .map((reason) => t(reason.key, { defaultValue: reason.defaultValue }))
    .join(' ');

  return `${summary} ${reasons}`.trim();
}

const MAX_LISTED_EMAILS = 5;

const STALE_INVITATION_CODE = 'invitation_not_sent';

const RECIPIENT_FAILURE_KEYS = {
  notAdded: 'sendForApprovalResult.recipientFailure.notAdded',
  andMore: 'sendForApprovalResult.recipientFailure.andMore',
} as const;

const GENERIC_RECIPIENT_REASON: Reason = {
  key: 'sendForApprovalResult.recipientFailure.reason.generic',
  defaultValue: 'Contact support if this keeps happening.',
};

const RECIPIENT_REASON_BY_ERROR_CODE: Record<string, Reason> = {
  seat_limit: {
    key: 'sendForApprovalResult.recipientFailure.reason.seatLimit',
    defaultValue: 'Your plan has no free seats.',
  },
  subscription_inactive: {
    key: 'sendForApprovalResult.recipientFailure.reason.subscriptionInactive',
    defaultValue: "Your subscription isn't active.",
  },
  removed_team_member: {
    key: 'sendForApprovalResult.recipientFailure.reason.removedTeamMember',
    defaultValue:
      'People who are no longer on your team can be restored by an admin in Users.',
  },
  demo_user: {
    key: 'sendForApprovalResult.recipientFailure.reason.demoUser',
    defaultValue: "Demo users can't approve posts.",
  },
  identity_unavailable: {
    key: 'sendForApprovalResult.recipientFailure.reason.identityUnavailable',
    defaultValue: "We couldn't check an address right now. Please try again.",
  },
  blocked: {
    key: 'sendForApprovalResult.recipientFailure.reason.blocked',
    defaultValue: "Some addresses can't be added. Please contact support.",
  },
};

function isRecipientFailure(value: unknown): value is ApprovalRecipientFailure {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ApprovalRecipientFailure).email === 'string' &&
    (value as ApprovalRecipientFailure).email !== ''
  );
}

/** The emails, each once ignoring case (the api lowercases them), in the order given. */
function distinctEmails(failures: ApprovalRecipientFailure[]): string[] {
  const seen = new Set<string>();

  return failures
    .map((failure) => failure.email)
    .filter((email) => {
      const key = email.toLowerCase();
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
}

/** The first `shown` emails, then "and N more" for the rest. */
function listEmails(emails: string[], shown: number, t: TFunction): string {
  const listed = emails.slice(0, shown).join(', ');
  const hidden = emails.length - shown;

  if (hidden <= 0) {
    return listed;
  }

  return `${listed} ${t(RECIPIENT_FAILURE_KEYS.andMore, {
    defaultValue: 'and {{count}} more',
    count: hidden,
  })}`;
}

/**
 * Every translation key the recipient message uses (plural keys by their base name), so a
 * test can check them against the locale files.
 */
export const RECIPIENT_FAILURE_TRANSLATION_KEYS: {
  keys: string[];
  pluralKeys: string[];
} = {
  keys: [
    RECIPIENT_FAILURE_KEYS.notAdded,
    GENERIC_RECIPIENT_REASON.key,
    ...Object.values(RECIPIENT_REASON_BY_ERROR_CODE).map(
      (reason) => reason.key,
    ),
  ],
  pluralKeys: [RECIPIENT_FAILURE_KEYS.andMore],
};

/**
 * Long enough to read a list of approvers: 60 ms per character, between 8 and 30 seconds
 * (the default warning and error toasts close after 6 seconds).
 */
export function recipientToastDuration(message: string): number {
  return NumberUtil.clamp(message.length * 60, 8000, 30000);
}

/**
 * The typed approvers that were not added, at most five named, then each distinct reason
 * once. Only the codes the api returns get their own reason (anything else is generic), so
 * the copy never tells whether a person has a login. Invitations go out in a queued job
 * after the send, so no invitation outcome is ever listed.
 */
export function buildRecipientFailureMessage(
  failures: unknown,
  t: TFunction,
): string | null {
  if (!Array.isArray(failures)) {
    return null;
  }

  // An older api reported a failed invitation with this code; that approver was added,
  // so it is not listed as one that couldn't be added.
  const recipientFailures = failures
    .filter(isRecipientFailure)
    .filter((failure) => failure.error_code !== STALE_INVITATION_CODE);

  if (recipientFailures.length === 0) {
    return null;
  }

  const sentences = [
    t(RECIPIENT_FAILURE_KEYS.notAdded, {
      defaultValue: "These approvers couldn't be added: {{emails}}.",
      emails: listEmails(
        distinctEmails(recipientFailures),
        MAX_LISTED_EMAILS,
        t,
      ),
    }),
  ];

  const seenKeys = new Set<string>();
  recipientFailures
    .map(
      (failure) =>
        RECIPIENT_REASON_BY_ERROR_CODE[failure.error_code ?? ''] ??
        GENERIC_RECIPIENT_REASON,
    )
    .filter((reason) => {
      if (seenKeys.has(reason.key)) {
        return false;
      }
      seenKeys.add(reason.key);
      return true;
    })
    .forEach((reason) =>
      sentences.push(t(reason.key, { defaultValue: reason.defaultValue })),
    );

  return sentences.join(' ');
}

type ApprovalRecipientsErrorBody = {
  error_data?: { failed_recipients?: unknown };
};

export type SendForApprovalToast = {
  message: string;
  type: 'warning' | 'error';
  options?: { duration: number };
};

/** The warning toast for the typed approvers that failed, or null when none did. */
export function recipientFailureToast(
  failures: unknown,
  t: TFunction,
): SendForApprovalToast | null {
  const message = buildRecipientFailureMessage(failures, t);

  return message === null
    ? null
    : {
        message,
        type: 'warning',
        options: { duration: recipientToastDuration(message) },
      };
}

/**
 * The toasts for a failed send: when every typed approver failed (422), one error toast
 * listing them; otherwise the posts error, then the approvers that failed when every post
 * failed (M-12).
 */
export function sendForApprovalErrorToasts(
  error: ApiClientError | undefined,
  t: TFunction,
): SendForApprovalToast[] {
  const fallback = error?.userMessage ?? t('somethingWentWrongTryAgain');

  if (getErrorCode(error) === 'APPROVAL_RECIPIENTS_FAILED') {
    const body = getErrorResponse(error)?.data as
      | ApprovalRecipientsErrorBody
      | undefined;
    const message =
      buildRecipientFailureMessage(body?.error_data?.failed_recipients, t) ??
      fallback;

    return [
      {
        message,
        type: 'error',
        options: { duration: recipientToastDuration(message) },
      },
    ];
  }

  const recipientToast = recipientFailureToast(
    (error?.response as SendForApprovalResult | undefined)?.failed_recipients,
    t,
  );

  return [
    { message: fallback, type: 'error' },
    ...(recipientToast ? [recipientToast] : []),
  ];
}

type Params = {
  submitSendForApproval: (params: Omit<SendForApprovalParams, 'type'>) => void;
  isLoading: boolean;
};

type Props = {
  children: (params: Params) => JSX.Element;
  successCallback?: (result: { hasPartialFailure: boolean }) => void;
  type: ApprovalType;
};

const RequestContainer: FC<Props> = ({ children, successCallback, type }) => {
  const toast = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const user = useUser();
  const openFirstApprovalRequestedModal = useFirstApprovalRequestedModal(
    (state) => state.open,
  );
  const { mutate: mutateSendForApproval, isLoading: isSendForApprovalLoading } =
    useMutation(PostTimeSaversApi.sendForApproval, {
      onSuccess: (data) => {
        const partialFailureMessage = buildSendForApprovalFailureMessage(
          data?.batchResult,
          t,
        );

        if (partialFailureMessage) {
          toast(partialFailureMessage, 'warning');
        }

        const recipientToast = recipientFailureToast(
          data?.batchResult?.failed_recipients,
          t,
        );

        if (recipientToast) {
          toast(
            recipientToast.message,
            recipientToast.type,
            recipientToast.options,
          );
        }

        const isFirstClientApprovalRequest =
          type === 'client' &&
          !hasPostSentForApproval(user.id, 'client') &&
          getUserFirstTimeFlagStatus(
            user.firstTimeFlags,
            FirstTimeFlagKey.FIRST_CLIENT_APPROVAL_REQUESTED,
          ) === false;

        queryClient.invalidateQueries(queryKey.dashboardPosts());
        queryClient.invalidateQueries(queryKey.postRevisions());
        queryClient.invalidateQueries(queryKey.postsMeta());
        dispatch(
          UserActions.setFirstTimeFlag(
            type === 'client'
              ? FirstTimeFlagKey.FIRST_CLIENT_APPROVAL_REQUESTED
              : FirstTimeFlagKey.FIRST_INTERNAL_APPROVAL_REQUESTED,
          ),
        );
        markPostSentForApproval(user.id, type);
        successCallback?.({
          hasPartialFailure: partialFailureMessage !== null,
        });

        if (isFirstClientApprovalRequest) {
          openFirstApprovalRequestedModal();
        }
      },
      onError: (error: ApiClientError) => {
        sendForApprovalErrorToasts(error, t).forEach(
          ({ message, type: toastType, options }) =>
            toast(message, toastType, options),
        );
        Logger.error(error);
      },
    });

  const handleSubmitSendForApproval = (
    formValues: Omit<SendForApprovalParams, 'type'>,
  ) => {
    mutateSendForApproval({ type, ...formValues });
  };

  return children({
    submitSendForApproval: handleSubmitSendForApproval,
    isLoading: isSendForApprovalLoading,
  });
};

export default RequestContainer;
